import React from 'react';
import moment from 'moment/moment';
import RecommendationDetails from '../../recommendationGenericComponents/recommendationDetails';
import { AZURE_PROPERTIES, BASE_PROPERTIES, COST_PROPERTIES, DISK_PROPERTIES } from '../../recommendationPropertyUtils';
import RecommendationDetailsLayout from '../../recommendationDetailsLayout';
import RecommendationPreferences from '../../recommendationGenericComponents/recommendationPreferences';
import RecommendationInsideLook from '../../recommendationGenericComponents/recommendationInsideLook';
import OtherResourceIdRecommendations from '../../recommendationGenericComponents/otherResourceIdRecommendations';
import RecommendationChartData, {
  CHART_TYPES,
} from 'recommendationsNew/components/detailedRecommendation/recommendationGenericComponents/recommendationChart/recommendationChartData.jsx';
import { Y_AXIS_UNIT } from 'recommendationsNew/components/detailedRecommendation/recommendationGenericComponents/recommendationChart/chartConsts.js';

const AZURE_DISK_TYPE_CHANGE_PROPERTIES = {
  DISK_ID: {
    label: 'Disk ID',
    getterFunction: (recommendation) => recommendation?.recData?.disk_id,
  },
  DISK_STARTING_TIME: {
    label: 'Starting Time',
    getterFunction: (recommendation) => {
      if (recommendation?.recData?.disk_time_created) {
        return typeof recommendation?.recData?.disk_time_created === 'string'
          ? recommendation?.recData?.disk_time_created
          : moment(recommendation?.recData?.disk_time_created * 1000).format('YYYY-MM-DD HH:mm:ss');
      }
      return undefined;
    },
  },
  NEW_DISK_TYPE: {
    label: 'New Disk Type',
    getterFunction: (recommendation) => recommendation?.recData?.new_disk_type,
  },
  IOPS: {
    label: 'IOPS',
    getterFunction: (recommendation) => recommendation?.recData?.iops,
  },
  ATTACHED_VMS: {
    label: 'Attached VMs',
    getterFunction: (recommendation) => recommendation?.recData?.disk_managed_by?.split('/')?.pop(),
  },
  TOTAL_IOPS: {
    label: 'Total IOPS',
    getterFunction: (recommendation) => recommendation?.recData?.total_iops,
  },
  AVG_IOPS_UTILIZATION: {
    label: 'Avg IOPS Utilization',
    getterFunction: (recommendation) => recommendation?.recData?.avg_iops_util,
  },
  Min_IOPS_UTILIZATION: {
    label: 'Min IOPS Utilization',
    getterFunction: (recommendation) => recommendation?.recData?.min_iops_util,
  },
  MAX_IOPS_UTILIZATION: {
    label: 'Max IOPS Utilization',
    getterFunction: (recommendation) => recommendation?.recData?.max_iops_util,
  },
  AVG_THROUGHPUT_UTILIZATION: {
    label: 'Avg Throughput Utilization',
    getterFunction: (recommendation) => recommendation?.recData?.avg_bandwidth_util,
  },
  MIN_THROUGHPUT_UTILIZATION: {
    label: 'Min Throughput Utilization',
    getterFunction: (recommendation) => recommendation?.recData?.min_bandwidth_util,
  },
  MAX_THROUGHPUT_UTILIZATION: {
    label: 'Max Throughput Utilization',
    getterFunction: (recommendation) => recommendation?.recData?.max_bandwidth_util,
  },
};

const AzureVmRightSizing = ({ recommendation }) => {
  const properties = {
    ...DISK_PROPERTIES,
    ...AZURE_DISK_TYPE_CHANGE_PROPERTIES,
    ...AZURE_PROPERTIES,
    ...COST_PROPERTIES,
    ...BASE_PROPERTIES,
  };

  const description = `This managed disk has low IOPs utilization, we recommend changing its configuration to a more suitable type, which will cost less.`;
  const insideLookComponent = <RecommendationInsideLook description={description} />;

  const otherResourceIdRecommendations = <OtherResourceIdRecommendations resourceId={recommendation?.resourceId} />;

  const detailsComponent = (
    <RecommendationDetails recommendationProperties={properties} recommendation={recommendation} />
  );

  const preferencesComponent = <RecommendationPreferences recommendationType={recommendation.typeId} />;

  const chartsData = [
    {
      chartType: CHART_TYPES.SERIES_DATA,
      chartTitle: 'Disk IOPS Utilization',
      yAxisLabel: 'IOPS',
      yAxisUnit: Y_AXIS_UNIT.PERCENT,
      info: 'The percentage calculated by the data disk IOPS completed over the provisioned data disk IOPS.',
      maxDataProperty: recommendation.recData?.iops_max_timeseries,
      avgDataProperty: recommendation.recData?.iops_avg_timeseries,
      minDataProperty: recommendation.recData?.iops_min_timeseries,
    },
    {
      chartType: CHART_TYPES.SERIES_DATA,
      chartTitle: 'Throughput Utilization',
      yAxisLabel: 'Bandwidth',
      yAxisUnit: Y_AXIS_UNIT.PERCENT,
      info: 'The percentage calculated by the data disk throughput completed over the provisioned data disk throughput.',
      maxDataProperty: recommendation.recData?.bandwidth_max_timeseries,
      avgDataProperty: recommendation.recData?.bandwidth_avg_timeseries,
      minDataProperty: recommendation.recData?.bandwidth_min_timeseries,
    },
  ];
  const chartsComponent = <RecommendationChartData chartsData={chartsData} />;

  return (
    <RecommendationDetailsLayout
      insideLook={insideLookComponent}
      otherResourceIdRecommendations={otherResourceIdRecommendations}
      details={detailsComponent}
      charts={chartsComponent}
      preferences={preferencesComponent}
    />
  );
};

export default AzureVmRightSizing;
